// lib/adopt-project-generation.ts
import type { GenerationTask, KernelHandle, Layer } from '#types/types';
import { areGenerationOutputScopesEqual, normalizeGenerationOutputScope } from '#lib/generation-output-scope';

export async function adoptProjectGeneration(
  kernel: Pick<KernelHandle, 'syncEntity' | 'getLayerRegistry'>,
  task: GenerationTask,
  result: { generationTaskId?: string | null; outputScope?: unknown; layerId?: string | null; layerIds?: string[] | null },
): Promise<Layer[]> {
  const scope = normalizeGenerationOutputScope(result.outputScope);
  if (scope?.kind !== 'PROJECT' || !areGenerationOutputScopesEqual(scope, task.outputScope)) throw new Error('The saved output belongs to a different project.');
  const ids = result.layerIds?.length ? [...new Set(result.layerIds)] : result.layerId ? [result.layerId] : [];
  if (!ids.length || ids.length > 16 || ids.some(id => !result.generationTaskId || !id.startsWith(`generation-${result.generationTaskId}-`))) {
    throw new Error('The generation did not return saved output identities.');
  }
  const layers: Layer[] = [];
  for (const id of ids) {
    // Fresh read respects deletion and edits made after the terminal event was originally sent.
    const dto = await kernel.syncEntity('StudioLayer', id, { force: true, requireFresh: true });
    if (!dto) continue;
    const layer = kernel.getLayerRegistry()[id];
    if (!layer || layer.projectId !== scope.projectId || layer.layerWorkspaceId) throw new Error('The saved output has an invalid destination.');
    layers.push(layer);
  }
  return layers;
}
